import { Query } from '@flakiness/server/common/fql/query.js';

import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { type WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK, xxHashObject } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { api } from '../api.js';
import { linkStyles, pageStyles } from '../components/cssstyles.js';
import { DataTableLinker, DataTableProvider } from '../components/data-table.js';
import { FqlChangedEvent } from '../components/fql-search.js';
import { FKReportHistorySelectEvent } from '../components/report-history-selector.js';
import { ContextTask, consume, contexts } from '../contexts.js';
import { createAnnotationStatsDataProvider, createAnnotationStatsLoaderRemote } from '../data-table-providers/annotationsProvider.js';
import { createErrorStatsDataProvider, createErrorStatsLoaderRemote } from '../data-table-providers/errorsProvider.js';
import { createDurationTrendColumn, createTestHistoryColumn } from '../data-table-providers/genericColumns.js';
import { createTagStatsDataProvider, createTagStatsLoaderRemote } from '../data-table-providers/tagsProvider.js';
import { createStatusColumn, createTestStatsLoaderRemote, testStatsColumns } from '../data-table-providers/testStatsProvider.js';
import { createTimelineStatsDataProvider, createTimelineStatsLoaderRemote } from '../data-table-providers/timelineStatsProvider.js';
import { FKHistoryRequested, FeatureCommitHistoryPopup } from '../features/feature-commits-history.js';
import { AppLink, Linkifier, handleLink } from '../linkifier.js';
import { PageRun } from '../page-run.js';

export type PlaneName = 'tests'|'timelines'|'errors'|'tags'|'annotations';

export type FeatureTestsReportState = {
  timelineSplit: EnvGroup,
  query?: string,
  plane?: PlaneName,
  page?: number,
  pageSize?: number,
  sortName?: string,
  sortDirection?: WireTypes.SortDirection,
  // Local override of the project's default history type. Absent => follow the project default.
  historyType?: WireTypes.HistoryType,
};

@customElement('feature-tests-report')
export class FeatureTestsReport extends LitElement {

  @query('feature-commit-history-popup') private _featureCommitHistoryPopup?: FeatureCommitHistoryPopup;

  // Page guides point at these.
  @query('fql-search') fqlSearchElement?: HTMLElement;
  @query('report-outcomes') reportOutcomesElement?: HTMLElement;
  @query('fk-tabbar') testsTableElement?: HTMLElement;

  @consume({ context: contexts.commitRegistry, subscribe: true }) private _commitRegistry?: ContextType<typeof contexts.commitRegistry>;  
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) commitOptions?: WireTypes.ListCommitOptions;
  @property({ attribute: false }) customHistoryHead?: string;
  @property({ attribute: false }) historyBranchName?: string;
  @property({ attribute: false }) state?: FeatureTestsReportState;
  @property({ attribute: false }) linkifier?: Linkifier<FeatureTestsReportState>;

  @property({ attribute: false }) viewSince?: Temporal.PlainDate;
  @property({ attribute: false }) viewUntil?: Temporal.PlainDate;
  @property({ attribute: false }) until?: Temporal.PlainDate;

  private _linkify(update: Partial<FeatureTestsReportState>): AppLink {
    if (!this.state)
      return;
    const newState = { ...this.state, ...update };
    return this.linkifier?.(newState) ?? (() => {
      this.state = newState
    });
  }

  private _fqlTask = new ContextTask(this, contexts.fql, {
    args: () => [this.state?.query] as const,
    task: async ([query], { signal }) => {
      return Query.parse(query ?? '');
    }
  });

  private _fqlLinker = new ContextTask(this, contexts.linkFQL, {
    args: () => [this.linkifier],
    task: async ([], { signal }) => {
      return query => this._linkify({ query, page: 0 });
    }
  });

  private _timelineLinker = new ContextTask(this, contexts.linkTimeline, {
    args: () => [this.linkifier],
    task: async ([], { signal }) => {
      return timeline => this._linkify({ timelineSplit: timeline });
    }
  });

  private _reportOutcomes = new Task(this, {
    args: () => [
      this._reportOptionsTask.value,
    ] as const,
    argsEqual: (oldArgs, newArgs) => xxHashObject(oldArgs) === xxHashObject(newArgs),
    task: async ([reportOptions], { signal }) => {
      CHECK(reportOptions);
      const result = await api.report.counters.POST({
        ...reportOptions,
        fql: Query.parse(reportOptions.fql).clearStatusFilters().serialize(),
      }, { signal });
      return result.testOutcomes;
    }
  });

  private _tabCountsTask = new Task(this, {
    args: () => [
      this._reportOptionsTask.value,
    ] as const,
    task: async ([reportOptions], { signal }) => {
      CHECK(reportOptions);
      const result = await api.report.counters.POST(reportOptions, { signal });
      return result;
    }
  });

  private _testLinkRenderer = new ContextTask(this, contexts.linkTest, {
    args: () => [this._project, this._snapshotId, this.state?.timelineSplit, this.state?.historyType] as const,
    task: async ([project, snapshotId, timelineSplit, historyType], { signal }) => {
      CHECK(project && timelineSplit);
      return (testStats, revealId) => PageRun.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: testStats.runId,
        timelineSplit,
        testId: testStats.test.testId,
        testTimeline: EnvGroup.deserialize(testStats.timeline),
        historyType,
        snapshotId,
      }) + (revealId ? '#' + revealId : '');
    }
  });

  private _reportOptionsTask = new Task(this, {
    args: () => [
      this._project,
      this._snapshotId,
      this._fqlTask.value,
      this.state?.timelineSplit,
      this.commitOptions,
      this.customHistoryHead,
      this.state?.historyType,
    ] as const,
    task: async ([project, snapshotId, fql, split, commitOptions, customHistoryHead, historyType], { signal }) => {
      CHECK(project && snapshotId && fql && split && commitOptions);

      return {
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        fql: fql.serialize(),
        timelineSplit: split.serialize(),
        commitOptions,
        // URL override wins; otherwise follow the project's default history type.
        historyConfig: { type: historyType ?? project.defaultHistoryType ?? 'commit', customHistoryHead },
        timeZoneId: project.timezone,
        acceptableFlakinessRate: project.acceptableFlakinessRatio,
      } satisfies WireTypes.ReportOptions;
    }
  });


  private _dataTableProviderTask = new Task(this, {
    args: () => [
      this._project, this._commitRegistry, this._reportOptionsTask.value, this.state?.plane, this.state?.timelineSplit, this.historyBranchName,
    ] as const,
    task: async ([project, commitRegistry, reportOptions, plane, timelineSplit, historyBranchName], { signal }) => {
      CHECK(project && commitRegistry && reportOptions && timelineSplit);

      plane ??= 'tests';

      if (plane === 'tests') {
        const historyColumn = createTestHistoryColumn<WireTypes.TestStats>({
          history: testStats => {
            // The appended PR entry is the merge commit — always a commit bar,
            // even when the base-branch history is bucketed by day.
            return this.customHistoryHead ? [
              {
                commitId: testStats.commitId,
                outcome: testStats.outcome,
                duration: testStats.durationMs,
                historyType: 'commit',
                sinceTimestamp: commitRegistry.commitOrFake(testStats.commitId).timestamp,
              },
              ...testStats.history,
            ] : testStats.history;
          },
          timezone: project.timezone,
          commitRegistry,
          setting: { settingId: 'test-stats.history', title: 'History' },
          name: html`<report-history-selector
            .historyType=${this.state?.historyType}
            .branchName=${historyBranchName}
            @fk-history-change=${(e: FKReportHistorySelectEvent) => handleLink(this._router, this._linkify({ historyType: e.historyType, page: 0 }))}
          ></report-history-selector>`,
          onHistorySelected: (testStats, selection) => {
            void this._featureCommitHistoryPopup?.showHistoryBar({
              testStats,
              selection,
              state: {
                timelineSplit,
                head: reportOptions.commitOptions.head,
                testId: testStats.test.testId,
                testTimeline: EnvGroup.deserialize(testStats.timeline),
                viewSince: this.viewSince,
                viewUntil: this.viewUntil,
              },
              historyHead: this.customHistoryHead,
            });
          },
        });
        const statusColumn = createStatusColumn({
          name: html`S`,
          commitRegistry,
          runLinker: (testStats) => PageRun.url({
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            reportId: testStats.runId,
            testTimeline: EnvGroup.deserialize(testStats.timeline),
            testId: testStats.test.testId,
            testAttempt: testStats.attemptIdx,
            timelineSplit: EnvGroup.deserialize(testStats.timeline),
            historyType: this.state?.historyType,
            snapshotId: reportOptions.snapshotId,
          }),
        });
        return {
          loader: createTestStatsLoaderRemote(commitRegistry, reportOptions),
          columns: [
            historyColumn,
            statusColumn,
            testStatsColumns.name,
            testStatsColumns.timeline,
            testStatsColumns.duration,
            createDurationTrendColumn<WireTypes.TestStats>(),
            testStatsColumns.flipRate,
            testStatsColumns.failRate,
          ].filter(c => !!c),
          defaultSortColumn: statusColumn,
        } satisfies DataTableProvider<WireTypes.TestStats, WireTypes.TestStatsSortAxis>;
      }
      if (plane === 'timelines') {
        return createTimelineStatsDataProvider({
          project,
          loader: createTimelineStatsLoaderRemote(reportOptions),
        });
      }
      if (plane === 'errors')
        return createErrorStatsDataProvider({ loader: createErrorStatsLoaderRemote(reportOptions)});
      if (plane === 'tags')
        return createTagStatsDataProvider({ loader: createTagStatsLoaderRemote(reportOptions)});
      if (plane === 'annotations')
        return createAnnotationStatsDataProvider({ loader: createAnnotationStatsLoaderRemote(reportOptions)});
    }
  });

  hideDialog() {
    this._featureCommitHistoryPopup?.hide();
  }

  override render() {
    if (!this._project || !this.state)
      return nothing;

    return html`
      <feature-commit-history-popup></feature-commit-history-popup>
      <v-box style="gap: var(--sl-spacing-large);"
        @fk-history-requested=${(event: FKHistoryRequested) => {
          this._featureCommitHistoryPopup?.show({
            testStats: event.testStats,
            state: {
              timelineSplit: this.state?.timelineSplit ?? EnvGroup.open(),
              head: this._reportOptionsTask.value?.commitOptions.head ?? event.testStats.commitId,
              testId: event.testStats.test.testId,
              testTimeline: EnvGroup.deserialize(event.testStats.timeline),
              viewSince: this.viewSince,
              viewUntil: this.viewUntil,
              until: this.until,
            }
          })
        }}
      >
        <h-box style="gap: var(--sl-spacing-3x-large);">
          <v-box style="flex: auto;">
            <slot name=header></slot>
            <h-box style="align-items: flex-start;">
              <fql-search q=${this.state?.query ?? ''} @fql-changed=${(e: FqlChangedEvent) => {
                handleLink(this._router, this._linkify({ query: e.query, page: 0 }));
              }}></fql-search>
            </h-box>
            <report-outcomes .outcomes=${this._reportOutcomes.value}></report-outcomes>

          </v-box>
          <slot name=sidebar></slot>
        </h-box>

        <fk-tabbar selected=${this.state?.plane ?? 'tests'}>
          <fk-tab
            name=tests
            data-testid=plane-tests
            .link=${this._linkify({ plane: 'tests', page: 0, sortDirection: undefined, sortName: undefined, })}
          >
            Tests (${this._tabCountsTask.value?.tests ?? 0})
          </fk-tab>
          <fk-tab
            name=timelines
            data-testid=plane-timelines
            .link=${this._linkify({ plane: 'timelines', page: 0, sortDirection: undefined, sortName: undefined, })}
          >
            Environments (${this._tabCountsTask.value?.timelines ?? 0})
          </fk-tab>
          <fk-tab
            name=errors
            data-testid=plane-errors
            .link=${this._linkify({ plane: 'errors', page: 0, sortDirection: undefined, sortName: undefined, })}
          >
            Errors (${this._tabCountsTask.value?.errors ?? 0})
          </fk-tab>
          <fk-tab
            name=tags
            data-testid=plane-tags
            .link=${this._linkify({ plane: 'tags', page: 0, sortDirection: undefined, sortName: undefined, })}
          >
            Tags (${this._tabCountsTask.value?.tags ?? 0})
          </fk-tab>
          <fk-tab
            name=annotations
            data-testid=plane-annotations
            .link=${this._linkify({ plane: 'annotations', page: 0, sortDirection: undefined, sortName: undefined, })}
          >
            Annotations (${this._tabCountsTask.value?.annotations ?? 0})
          </fk-tab>

          <fk-tabpane>
            <data-table
              .pageNumber=${this.state.page}
              .pageSize=${this.state.pageSize}
              .sortAxis=${this.state.sortName}
              .sortDirection=${this.state.sortDirection}
              .provider=${this._dataTableProviderTask.value}
              .linker=${this._linkify.bind(this) satisfies DataTableLinker}
            >
              <no-data-message
                .state=${this.state}
                .linker=${this._linkify.bind(this)}
              ></no-data-message>
            </data-table>
          </fk-tabpane>
        </fk-tabbar>
      </v-box>
    `
  }

  static styles = [pageStyles, linkStyles, css`
  `]
}
